import { writable, type Readable, type Writable } from "svelte/store";
import { defaultScanName, defaultPhotoName } from "../utils/filename";

export type Phase =
  | { kind: "idle" }
  | { kind: "processing"; pageCount: number }
  | { kind: "ready" }
  | { kind: "error"; message: string };

export type Output =
  | { kind: "pdf"; blob: Blob; url: string; previewUrls: string[] }
  | { kind: "image"; blob: Blob; url: string };

export interface PdfBuilder {
  build(pages: Blob[]): Promise<Blob>;
}

export interface PhotoWriter {
  write(image: Blob): Promise<Blob>;
}

export interface CaptureSession {
  phase: Readable<Phase>;
  output: Readable<Output | null>;
  suggestedName: Writable<string>;
  pageCount: Readable<number>;
  ingestImages(images: Blob[]): Promise<void>;
  ingestPhoto(image: Blob): Promise<void>;
  reset(): void;
}

export interface Deps {
  builder: PdfBuilder;
  writer: PhotoWriter;
  now?: () => Date;
}

export function createCaptureSession(deps: Deps): CaptureSession {
  const now = deps.now ?? (() => new Date());

  const phase: Writable<Phase> = writable({ kind: "idle" });
  const output: Writable<Output | null> = writable(null);
  const suggestedName: Writable<string> = writable(defaultScanName(now()));
  const pageCount: Writable<number> = writable(0);

  function revokeOutput(value: Output | null): void {
    if (!value) return;
    URL.revokeObjectURL(value.url);
  }

  function setOutput(blob: Blob, kind: "image"): Output;
  function setOutput(blob: Blob, kind: "pdf", previewUrls: string[]): Output;
  function setOutput(blob: Blob, kind: "pdf" | "image", previewUrls: string[] = []): Output {
    let current: Output | null = null;
    output.update((value) => {
      current = value;
      return value;
    });
    revokeOutput(current);
    const url = URL.createObjectURL(blob);
    const next: Output =
      kind === "pdf"
        ? { kind, blob, url, previewUrls }
        : { kind, blob, url };
    output.set(next);
    return next;
  }

  async function ingestImages(images: Blob[]): Promise<void> {
    if (images.length === 0) return;
    pageCount.set(images.length);
    suggestedName.set(defaultScanName(now()));
    phase.set({ kind: "processing", pageCount: images.length });
    try {
      const previews = await Promise.all(images.map(blobToDataUrl));
      const pdf = await deps.builder.build(images);
      setOutput(pdf, "pdf", previews);
      phase.set({ kind: "ready" });
    } catch {
      output.set(null);
      phase.set({ kind: "error", message: "Couldn't build PDF. Try again." });
    }
  }

  async function ingestPhoto(image: Blob): Promise<void> {
    pageCount.set(0);
    suggestedName.set(defaultPhotoName(now()));
    phase.set({ kind: "processing", pageCount: 0 });
    try {
      const jpg = await deps.writer.write(image);
      setOutput(jpg, "image");
      phase.set({ kind: "ready" });
    } catch {
      output.set(null);
      phase.set({ kind: "error", message: "Couldn't save photo. Try again." });
    }
  }

  function reset(): void {
    let current: Output | null = null;
    output.update((value) => {
      current = value;
      return value;
    });
    revokeOutput(current);
    output.set(null);
    pageCount.set(0);
    phase.set({ kind: "idle" });
    suggestedName.set(defaultScanName(now()));
  }

  return { phase, output, suggestedName, pageCount, ingestImages, ingestPhoto, reset };
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("Couldn't create preview."));
    reader.readAsDataURL(blob);
  });
}
